import { useState } from "react"
import { useMutation } from "react-query"

import { buildLaunchCart } from "src/api/DryStack"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import {
  MARINA,
  SURFACE_PROPERTY,
  estimatedTotal,
  requestedItemsProperty,
} from "../../AddOns/launchAddOnTracking"
import { fillUpAddOns } from "../LaunchFuelQuantityModal"

// The launches page runs on a hash router and POS runs on its own browser
// router under /point_of_sale, so react-router cannot carry the dockhand
// across. useServerCart reads cart_id off the query string on the way in.
const useLaunchCart = ({ marinaSlug, trackingProps, genericError }) => {
  const showToast = useToast()
  const tracker = useTracker()

  const [pendingFuelLaunch, setPendingFuelLaunch] = useState(null)

  const { mutate, isLoading } = useMutation(
    ({ encodedId, fuelQuantities }) =>
      buildLaunchCart(marinaSlug, encodedId, fuelQuantities),
    {
      onSuccess: (data) => {
        setPendingFuelLaunch(null)
        window.location.assign(
          `/manage/${marinaSlug}/point_of_sale?cart_id=${data.cart.id}`
        )
      },
      onError: (error) => {
        tracker.trackEvent("dry_stack_schedule_view:launch_cart_build_failed", {
          ...trackingProps,
          reason: error?.message,
        })
        showToast(error?.message || genericError, { type: "error" })
      },
    }
  )

  // Carrying a launch's requested items to the register in one click is the
  // shortcut the whole feature exists to provide, so these events answer
  // whether staff use it — and what they were carrying when they did.
  const cartProperties = (row) => ({
    ...trackingProps,
    surface: SURFACE_PROPERTY[MARINA],
    items: requestedItemsProperty(row?.addons),
    estimated_total: estimatedTotal(row?.addonEstimate),
  })

  const buildCart = (row) => {
    if (isLoading) return

    tracker.trackEvent(
      "dry_stack_schedule_view:launch_cart_started",
      cartProperties(row)
    )

    // A fill-up carries no amount until the pump stops, and the server refuses
    // to price one. Ask before posting rather than round-tripping to a 422 the
    // dockhand has no way to answer.
    if (fillUpAddOns(row.addons).length > 0) {
      setPendingFuelLaunch(row)
      return
    }

    mutate({ encodedId: row.encodedId, fuelQuantities: [] })
  }

  const confirmFuelQuantities = (fuelQuantities) => {
    if (isLoading || !pendingFuelLaunch) return

    tracker.trackEvent(
      "dry_stack_schedule_view:launch_cart_fuel_measured",
      cartProperties(pendingFuelLaunch)
    )
    mutate({ encodedId: pendingFuelLaunch.encodedId, fuelQuantities })
  }

  // Success redirects out of the page, so no event can fire there. Without this
  // one, a launch dropped at the fuel prompt is indistinguishable from one that
  // reached the register, and `launch_cart_started` cannot be read as a count
  // of handoffs.
  const cancelFuelQuantities = () => {
    tracker.trackEvent(
      "dry_stack_schedule_view:launch_cart_fuel_abandoned",
      cartProperties(pendingFuelLaunch)
    )
    setPendingFuelLaunch(null)
  }

  return {
    buildCart,
    cancelFuelQuantities,
    confirmFuelQuantities,
    isBuildingCart: isLoading,
    pendingFuelLaunch,
  }
}

export default useLaunchCart
